import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type {
  ComposerHistoryKind,
  ComposerHistoryWireEntry,
} from "@bash/shared";

/**
 * Composer history, local mirror — the settled exchanges of this browser's
 * converse lane. The durable, account-synced truth is the server's
 * `composer_history` table (GET /api/composer/history); this store exists for
 * instant render and as the fallback when that fetch fails. The history card
 * merges the two ({@link mergeHistory} — server wins on overlap).
 *
 * Written by useComposerConverse as each turn settles; newest first, capped,
 * persisted to localStorage so a reload never amnesias it. The mirror meets
 * the newest server page only (useComposerHistoryPages); older pages are the
 * server's alone.
 */

export type { ComposerHistoryKind };

/**
 * One exchange, in the wire's own shape (packages/shared composer/history.ts):
 * a server row carries every field it was served (tools, via, and the chat
 * fields: surface, host, author, reply, route, receipt, task); the mirror
 * writes only the base fields (question, kind, text, taskId) and `agent`.
 */
export type ComposerHistoryEntry = ComposerHistoryWireEntry & {
  /** Agents first (train 1b): the HANDLE of the agent this exchange belongs
   *  to. Absent = the lead, which is every row written before agents existed,
   *  so Bash's own history is the history it always was. Mirror-only: the
   *  server's read is already scoped to one agent (or to the lead). */
  agent?: string;
  /** The row's React key when it is not `id` (2026-09-28): set by
   *  {@link mergeHistory} only, on a server row that settled a mirror row by
   *  content, to that mirror row's id, so the row painted from the mirror
   *  keeps its node when the server page lands. Never stored. */
  key?: string;
};

/** The mirror's own cap: enough to scroll back through a real day, small
 *  enough to never bloat localStorage. The merged transcript is not capped. */
const MAX_ENTRIES = 50;

/** A local row duplicates a server row when the same ask settled the same way
 *  within this window (client and server stamp the same exchange seconds apart). */
const MERGE_WINDOW_MS = 10 * 60 * 1000;

/**
 * Does this server row settle that local one? The kinds must agree, with ONE exception: the
 * mirror can never learn 'run' (2a, T2-9). useComposerConverse records 'answered' for every
 * settled turn — it reads the converse reducer's phase, which knows nothing of a run target —
 * while the server files the same exchange as 'run' once a card ran under the apps flag. So the
 * server's 'run' settles a local 'answered' for the same ask in the same window.
 *
 * Without it every card run sent from the web composer paints TWICE, and durably: the mirror is
 * persisted to localStorage, so the duplicate survives every reload until it falls off the cap.
 */
const settles = (
  s: ComposerHistoryWireEntry,
  l: ComposerHistoryEntry
): boolean => s.kind === l.kind || (s.kind === "run" && l.kind === "answered");

/**
 * Merge the newest server page with the local mirror, newest first, uncapped.
 * Server rows are authoritative; a local row survives only when nothing on the
 * page matches it (offline moment, or a row from before the table existed).
 *
 * One id per exchange (2026-09-28): a web ask names its mirror row with the
 * request's own id, and the server stores that id as the row's key, so a
 * mirror row and its server twin meet here by id, exactly, and the row keeps
 * its React key when the page lands (the history card used to remount every
 * row on open). The content match (kind, question, window) is the LEGACY path,
 * for a mirror row the server never names: one persisted before the ids met, a
 * reach frame's (report-app mints its own row), and a typed ask the Line
 * carried for a client that sent no id (the Line then names its own turn).
 * It meets only the server rows no
 * mirror row names, so it never takes an exchange that has its own twin, and
 * the server row it settles carries the mirror row's id as its `key`: the row
 * painted from the mirror keeps its node even on the first open after the
 * upgrade.
 *
 * `olderOnServer`: the page came back full (it carried a `nextCursor`), so the
 * account has older pages the mirror never meets. A local row older than this
 * page's oldest row plus the window could be settled by a row on one of them;
 * it is left out rather than painted beside its own server twin when that
 * page loads. (The old 50-cap hid exactly these rows.) The floor never passes
 * the page's newest row: a page that spans a burst (fifty asks inside the
 * window) would otherwise hide the ask just sent, whose server row is not
 * written yet.
 */
export function mergeHistory(
  server: ComposerHistoryWireEntry[],
  local: ComposerHistoryEntry[],
  olderOnServer = false
): ComposerHistoryEntry[] {
  const stamps = server.map((s) => s.ts);
  const floor =
    olderOnServer && server.length > 0
      ? Math.min(Math.min(...stamps) + MERGE_WINDOW_MS, Math.max(...stamps))
      : -Infinity;
  const serverIds = new Set(server.map((s) => s.id));
  const mirrorIds = new Set(local.map((l) => l.id));
  const unnamed = server.filter((s) => !mirrorIds.has(s.id));
  /** Server row id → the mirror row id it settled by content (legacy). */
  const keys = new Map<string, string>();
  const survivors = local.filter((l) => {
    if (serverIds.has(l.id)) return false; // the same exchange, by id
    const twin = unnamed.find(
      (s) =>
        settles(s, l) &&
        s.question === l.question &&
        Math.abs(s.ts - l.ts) < MERGE_WINDOW_MS
    );
    if (twin) {
      if (!keys.has(twin.id)) keys.set(twin.id, l.id);
      return false;
    }
    return l.ts >= floor;
  });
  const rows = server.map((s): ComposerHistoryEntry => {
    const key = keys.get(s.id);
    return key ? { ...s, key } : s;
  });
  return [...rows, ...survivors].sort((a, b) => b.ts - a.ts);
}

/** The kinds whose `text` is not Bash's words (a title, a label), so the words after them are the
 *  row's `reply`: the server's REPLY_KINDS (apps/server/src/services/composer/history-tap.ts). */
const REPLY_KINDS: ReadonlySet<ComposerHistoryKind> =
  new Set<ComposerHistoryKind>([
    "task_created",
    "task_followup",
    "nami_saved",
    "app_control",
    "show_tasks",
  ]);

interface ComposerHistoryState {
  /** Newest first. */
  entries: ComposerHistoryEntry[];
  /** `id` and `ts` are minted here unless the caller names the row (a reach frame does, and a converse turn names it with its request id); a named row already held is a no-op. */
  add: (
    e: Omit<ComposerHistoryEntry, "id" | "ts"> & { id?: string; ts?: number }
  ) => void;
  clear: () => void;
}

export const useComposerHistoryStore = create<ComposerHistoryState>()(
  persist(
    (set) => ({
      entries: [],
      add: (e) =>
        set((s) => {
          // The reach rail is at-least-once, so a row named after its notice arrives more than once.
          // A converse turn is one exchange however many times it records (an action, then words
          // after it): the server writes it once, so the mirror keeps the first record, and the
          // words after an action ride it as `reply`, the rule the server's history tap applies.
          const held = e.id ? s.entries.find((x) => x.id === e.id) : undefined;
          if (held) {
            const words = e.kind === "answered" ? e.text.trim() : "";
            if (!words || held.reply || !REPLY_KINDS.has(held.kind)) return s;
            return {
              entries: s.entries.map((x) =>
                x === held ? { ...x, reply: words } : x
              ),
            };
          }
          return {
            entries: [
              { ...e, id: e.id ?? crypto.randomUUID(), ts: e.ts ?? Date.now() },
              ...s.entries,
            ].slice(0, MAX_ENTRIES),
          };
        }),
      clear: () => set({ entries: [] }),
    }),
    {
      // Same reload contract as the composer dots (founder 07-16 incident):
      // Cmd+R must not amnesia the transcript.
      name: "vestra:composer-history",
      storage: createJSONStorage(() => localStorage),
      // v1 (history as a chat): the mirror keeps the ask trimmed, as the
      // server stores it. A row persisted before that is trimmed once as it
      // loads, so it meets its server twin instead of painting beside it.
      version: 1,
      migrate: (persisted, version) => {
        const state = persisted as ComposerHistoryState;
        if (version >= 1 || !Array.isArray(state?.entries)) return state;
        return {
          ...state,
          entries: state.entries.map((e) =>
            typeof e.question === "string"
              ? { ...e, question: e.question.trim() }
              : e
          ),
        };
      },
    }
  )
);
